import {ref} from 'vue';
import {defineStore} from 'pinia';
import {Configuration, ResumeApi, type UpdateBatchExperiencesRequest} from '@api';
import type {EditExperience} from '~/features/experience/types/EditExperience';
import {resumeImportApiMapper, resumeImportDomainMapper} from '../helpers/ResumeImportExperienceMapper';
import type {FunnelCtaHandlerForNext} from '../types/FunnelCtaHandler';
import {ResumeImportEmptyResumeError} from '../types/ResumeImportError';
import type {ResumeImportExperience} from '../types/ResumeImportExperience';

export const useResumeImportStore = defineStore('resume-import', () => {
    // Funnel CTAs state(s)
    const isFunnelCtaNextEnabled = ref<boolean>(false);
    const resumeImportDuplicatesList = ref<string[][]>([]);
    const resumeImportExperienceList = ref<ResumeImportExperience[]>([]);
    // Private ref(s)
    const _apiConfiguration = new Configuration({basePath: ''});
    const _resumeApi = new ResumeApi(_apiConfiguration);
    const _registeredHandlerForCtaNext = ref<FunnelCtaHandlerForNext>();

    // Functions: CTA Next
    function invokeHandlerForCtaNext(): Promise<unknown> {
        if (!_registeredHandlerForCtaNext.value) {
            return Promise.resolve();
        }
        return _registeredHandlerForCtaNext.value();
    }
    function registerHandlerForCtaNext(handler: FunnelCtaHandlerForNext) {
        _registeredHandlerForCtaNext.value = handler;
    }
    function unregisterHandlerForCtaNext() {
        _registeredHandlerForCtaNext.value = undefined;
    }

    // Function: Resume
    async function resumeFileUpload(resume: File): Promise<ResumeImportExperience[]> {
        const request = resumeImportApiMapper.toGetPreviewFromResumeRequest(resume);

        const previewResult = await _resumeApi.getPreviewFromResume(request);

        if (!previewResult || !previewResult.experiences || previewResult.experiences?.length === 0) {
            throw new ResumeImportEmptyResumeError('Resume does not contain any experience');
        }
        resumeImportExperienceList.value = resumeImportDomainMapper.toResumeImportExperiences(previewResult);
        resumeImportDuplicatesList.value = resumeImportDomainMapper.toResumeImportDuplicateList(previewResult);

        return resumeImportExperienceList.value;
    }

    function updateExistingExperience(experienceToSave: EditExperience) {
        const xpIndex = resumeImportExperienceList.value.findIndex(
            (resumeImportExperience) => resumeImportExperience.id && resumeImportExperience.id === experienceToSave.id,
        );

        if (xpIndex >= 0) {
            const resumeImportExperienceToSave: ResumeImportExperience = {
                ...resumeImportExperienceList.value[xpIndex],
                id: experienceToSave.id,
                title: experienceToSave.title,
                description: experienceToSave.description,
                company: experienceToSave.company,
                companyId: experienceToSave.companyId,
                area: experienceToSave.area,
                location: experienceToSave.location,
                startDate: experienceToSave.startDate,
                endDate: experienceToSave.endDate,
                currentJob: experienceToSave.currentJob,
                asFreelancer: experienceToSave.asFreelancer,
                tags: experienceToSave.skills?.map((skill) => skill.label) ?? [],
                skills: experienceToSave.skills ?? [],
            };

            resumeImportExperienceList.value[xpIndex] = resumeImportExperienceToSave;
        } else {
            throw new Error('Experience not found');
        }
    }

    async function updateExperienceList(): Promise<void> {
        const request: UpdateBatchExperiencesRequest = resumeImportApiMapper.toUpdateBatchExperiencesRequest(resumeImportExperienceList.value);
        await _resumeApi.updateBatchExperiences(request);
    }

    return {
        // refs
        isFunnelCtaNextEnabled,
        resumeImportDuplicatesList,
        resumeImportExperienceList,
        // functions
        invokeHandlerForCtaNext,
        registerHandlerForCtaNext,
        resumeFileUpload,
        updateExistingExperience,
        updateExperienceList,
        unregisterHandlerForCtaNext,
    };
});
